import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Spin, Typography } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import AuthLayout from '../../layouts/AuthLayout';
import accountDeletionService from '../../services/accountDeletion';
import authService from '../../services/auth';
import {
  CONFIRM_ALREADY_DELETED_MESSAGE,
  CONFIRM_GENERIC_ERROR_MESSAGE,
  CONFIRM_INVALID_CODE_MESSAGE,
  CONFIRM_LOADING_MESSAGE,
  CONFIRM_MISSING_CODE_MESSAGE,
  CONFIRM_OFFLINE_ERROR_MESSAGE,
  CONFIRM_SUCCESS_DESCRIPTION,
  CONFIRM_SUCCESS_TITLE,
} from './deleteAccountContent';
import '../../styles/support.css';
import '../../styles/delete-account.css';

const { Paragraph, Text, Title } = Typography;

const STATUS = {
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
};

const ERROR_TYPE = {
  MISSING_CODE: 'MISSING_CODE',
  INVALID_DELETION_CODE: 'INVALID_DELETION_CODE',
  CLIENT_ALREADY_DELETED: 'CLIENT_ALREADY_DELETED',
  OFFLINE: 'OFFLINE',
  GENERIC: 'GENERIC',
};

export default function ConfirmAccountDeletionPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const hasRequestedRef = useRef(false);

  const [status, setStatus] = useState(STATUS.LOADING);
  const [errorType, setErrorType] = useState(null);

  const confirmDeletion = useCallback(async (deletionCode) => {
    setStatus(STATUS.LOADING);
    setErrorType(null);

    if (!deletionCode?.trim()) {
      setErrorType(ERROR_TYPE.MISSING_CODE);
      setStatus(STATUS.ERROR);
      return;
    }

    try {
      await accountDeletionService.confirmAccountDeletion({
        code: deletionCode.trim(),
      });

      if (authService.isAuthenticated()) {
        await authService.logout();
      }

      setStatus(STATUS.SUCCESS);

      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      if (!navigator.onLine) {
        setErrorType(ERROR_TYPE.OFFLINE);
        setStatus(STATUS.ERROR);
        return;
      }

      const errorCode =
        err?.__normalized?.code ??
        err?.response?.data?.error?.code ??
        err?.response?.data?.code;

      if (errorCode === 'INVALID_DELETION_CODE') {
        setErrorType(ERROR_TYPE.INVALID_DELETION_CODE);
      } else if (errorCode === 'CLIENT_ALREADY_DELETED') {
        setErrorType(ERROR_TYPE.CLIENT_ALREADY_DELETED);
      } else {
        setErrorType(ERROR_TYPE.GENERIC);
      }

      setStatus(STATUS.ERROR);
    }
  }, [navigate]);

  useEffect(() => {
    if (hasRequestedRef.current) {
      return;
    }

    hasRequestedRef.current = true;
    confirmDeletion(code);
  }, [code, confirmDeletion]);

  const handleRetry = () => {
    confirmDeletion(code);
  };

  const renderContent = () => {
    if (status === STATUS.LOADING) {
      return (
        <div className="delete-account-success">
          <Spin size="large" style={{ marginBottom: 24 }} />
          <Paragraph strong style={{ fontSize: 16, marginBottom: 0 }}>
            {CONFIRM_LOADING_MESSAGE}
          </Paragraph>
        </div>
      );
    }

    if (status === STATUS.SUCCESS) {
      return (
        <div className="delete-account-success">
          <CheckCircleOutlined
            style={{ fontSize: 48, color: 'var(--sthenus-primary)', marginBottom: 16 }}
          />
          <Paragraph strong style={{ fontSize: 16, marginBottom: 8 }}>
            {CONFIRM_SUCCESS_TITLE}
          </Paragraph>
          <Text type="secondary">{CONFIRM_SUCCESS_DESCRIPTION}</Text>
          <div className="delete-account-form-actions" style={{ justifyContent: 'center', marginTop: 24 }}>
            <Button type="primary" onClick={() => navigate('/login')}>
              Ir al inicio
            </Button>
          </div>
        </div>
      );
    }

    if (errorType === ERROR_TYPE.MISSING_CODE) {
      return (
        <div className="delete-account-success">
          <CloseCircleOutlined
            style={{ fontSize: 48, color: '#ff4d4f', marginBottom: 16 }}
          />
          <Title level={4} style={{ marginBottom: 8 }}>
            Enlace no válido
          </Title>
          <Text type="secondary">{CONFIRM_MISSING_CODE_MESSAGE}</Text>
          <div className="delete-account-form-actions" style={{ justifyContent: 'center', marginTop: 24 }}>
            <Button type="primary" onClick={() => navigate('/eliminar-datos')}>
              Solicitar eliminación
            </Button>
            <Button onClick={() => navigate('/login')}>Regresar al inicio</Button>
          </div>
        </div>
      );
    }

    if (errorType === ERROR_TYPE.INVALID_DELETION_CODE) {
      return (
        <div className="delete-account-success">
          <ExclamationCircleOutlined
            style={{ fontSize: 48, color: '#faad14', marginBottom: 16 }}
          />
          <Title level={4} style={{ marginBottom: 8 }}>
            Enlace inválido o expirado
          </Title>
          <Text type="secondary">{CONFIRM_INVALID_CODE_MESSAGE}</Text>
          <div className="delete-account-form-actions" style={{ justifyContent: 'center', marginTop: 24 }}>
            <Button type="primary" onClick={() => navigate('/eliminar-datos')}>
              Solicitar un nuevo enlace
            </Button>
            <Button onClick={() => navigate('/support')}>Centro de ayuda</Button>
          </div>
        </div>
      );
    }

    if (errorType === ERROR_TYPE.CLIENT_ALREADY_DELETED) {
      return (
        <div className="delete-account-success">
          <InfoCircleOutlined
            style={{ fontSize: 48, color: 'var(--sthenus-primary)', marginBottom: 16 }}
          />
          <Title level={4} style={{ marginBottom: 8 }}>
            Cuenta ya eliminada
          </Title>
          <Text type="secondary">{CONFIRM_ALREADY_DELETED_MESSAGE}</Text>
          <div className="delete-account-form-actions" style={{ justifyContent: 'center', marginTop: 24 }}>
            <Button type="primary" onClick={() => navigate('/login')}>
              Regresar al inicio
            </Button>
          </div>
        </div>
      );
    }

    const errorMessage =
      errorType === ERROR_TYPE.OFFLINE
        ? CONFIRM_OFFLINE_ERROR_MESSAGE
        : CONFIRM_GENERIC_ERROR_MESSAGE;

    return (
      <div className="delete-account-success">
        <CloseCircleOutlined
          style={{ fontSize: 48, color: '#ff4d4f', marginBottom: 16 }}
        />
        <Title level={4} style={{ marginBottom: 8 }}>
          No se pudo confirmar
        </Title>
        <Text type="secondary">{errorMessage}</Text>
        <div className="delete-account-form-actions" style={{ justifyContent: 'center', marginTop: 24 }}>
          <Button type="primary" onClick={handleRetry}>
            Reintentar
          </Button>
          <Button onClick={() => navigate('/login')}>Regresar al inicio</Button>
        </div>
      </div>
    );
  };

  return (
    <AuthLayout>
      <Card
        className="support-form-card"
        style={{
          maxWidth: 560,
          width: '100%',
          borderRadius: 24,
          padding: '32px 40px',
          boxShadow: '0 20px 60px rgba(15, 23, 42, 0.16)',
        }}
        bodyStyle={{ padding: 0 }}
      >
        {renderContent()}
      </Card>
    </AuthLayout>
  );
}
